import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import * as FA from 'react-icons/fa'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import Dre from 'services/Dre'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import DreExpenseModal from 'pages/Modals/DreExpenseModal'

import {
  ExpensesContainer,
  Section,
  SectionTitle,
  HintText,
  ActionButton,
  MonthTitle,
  ExpenseRow,
  ExpenseInfo,
  ExpenseName,
  ExpenseMeta,
  IconButton,
  WarningText,
  MoneyText
} from './styles'

const KIND_LABELS = { fixed: 'Fixa', variable: 'Variável' }

export default function DreExpenses({ year, costs, expenses, onChange: handleChange }) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const canEdit = AuthService.hasPermission('financial.dre', 'edit')

  const [categories, setCategories] = useState([])
  const [modalExpense, setModalExpense] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    loadOptions()
  }, [])

  const loadOptions = async () => {
    try {
      const { data: loadedCategories } = await cutileiApi.get(`/businesses/${businessId}/dre_expense_categories`, requestAuth)
      setCategories(loadedCategories)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    }
  }

  const deleteExpense = async expense => {
    try {
      setErrorMessage(null)
      await cutileiApi.delete(`/businesses/${businessId}/dre_expenses/${expense.id}`, requestAuth)
      handleChange()
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    }
  }

  const openModal = expense => {
    setModalExpense(expense)
    setModalVisible(true)
  }

  const expensesByMonth = expenses.reduce((groups, expense) => {
    const month = expense.competence_month.slice(0, 7)
    return { ...groups, [month]: [...(groups[month] ?? []), expense] }
  }, {})

  // costs.cardFees usa a chave "MMM yyyy" da API; aqui vira "yyyy-MM" como as despesas.
  const cardFeesByMonth = Dre.getMonthKeys(year).reduce((fees, key, index) => {
    const value = Number(costs?.cardFees?.[key]) || 0
    if (value <= 0) return fees

    return { ...fees, [`${year}-${String(index + 1).padStart(2, '0')}`]: value }
  }, {})

  const months = [...new Set([...Object.keys(expensesByMonth), ...Object.keys(cardFeesByMonth)])].sort()

  return (
    <ExpensesContainer>
      <Section>
        <SectionTitle>Despesas de {year}</SectionTitle>
        <HintText>
          Comissões, bônus dos profissionais e taxas de maquininha entram sozinhos na DRE. Lance aqui as demais despesas.
        </HintText>
        {canEdit && (
          <ActionButton type='button' onClick={() => openModal(null)}>+ Nova despesa</ActionButton>
        )}
        {months.length === 0 && <HintText>Nenhuma despesa lançada em {year}.</HintText>}
        {months.map(month => (
          <div key={month}>
            <MonthTitle>{DateTime.fromISO(month).setLocale('pt-BR').toFormat('LLLL')}</MonthTitle>
            {cardFeesByMonth[month] > 0 && (
              <ExpenseRow>
                <ExpenseInfo>
                  <ExpenseName>Taxas de maquininha</ExpenseName>
                  <ExpenseMeta>Variável · lançada automaticamente</ExpenseMeta>
                </ExpenseInfo>
                <MoneyText kind='total' value={-cardFeesByMonth[month]}/>
              </ExpenseRow>
            )}
            {(expensesByMonth[month] ?? []).map(expense => (
              <ExpenseRow key={expense.id}>
                <ExpenseInfo>
                  <ExpenseName>{expense.category?.name}</ExpenseName>
                  <ExpenseMeta>
                    {KIND_LABELS[expense.category?.kind]}
                    {expense.description ? ` · ${expense.description}` : ''}
                  </ExpenseMeta>
                </ExpenseInfo>
                <MoneyText kind='total' value={-expense.value}/>
                {canEdit && (
                  <>
                    <IconButton type='button' aria-label='Editar despesa' onClick={() => openModal(expense)}>
                      <FA.FaPen color='#252525' size={14}/>
                    </IconButton>
                    <IconButton type='button' aria-label='Excluir despesa' onClick={() => deleteExpense(expense)}>
                      <FA.FaTrash color='#FF3939' size={14}/>
                    </IconButton>
                  </>
                )}
              </ExpenseRow>
            ))}
          </div>
        ))}
      </Section>
      {errorMessage && <WarningText>{errorMessage}</WarningText>}
      <DreExpenseModal
        visible={modalVisible}
        year={year}
        expense={modalExpense}
        categories={categories}
        onCategoryCreated={category => setCategories([...categories, category])}
        onConfirm={() => handleChange()}
        onClose={() => setModalVisible(false)}
      />
    </ExpensesContainer>
  )
}
